import { createContext, ReactNode, useCallback, useContext, useId, useMemo, useState } from "react";

type TabContextState<TabType> = {
	tabs: Map<TabType, string>;
	addTab: (type: TabType) => void;
	getTabIdByType: (type: TabType) => string | undefined;
};

export function createTabsContext<TabType>() {
	const TabsContext = createContext<TabContextState<TabType> | null>(null);

	const TabsProvider = ({ children }: { children: ReactNode }) => {
		const [tabs, setTabs] = useState<Map<TabType, string>>(new Map());
		const tabIdSuffix = useId();

		const addTab = useCallback(
			(type: TabType) => {
				setTabs((prev) => new Map(prev).set(type, `${String(type)}-tab-${tabIdSuffix}`));
			},
			[tabIdSuffix],
		);

		const getTabIdByType = useCallback((type: TabType) => tabs.get(type), [tabs]);

		const value = useMemo(() => ({ tabs, addTab, getTabIdByType }), [tabs, addTab, getTabIdByType]);

		return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
	};

	const useTabContext = (): TabContextState<TabType> => {
		const context = useContext(TabsContext);
		if (!context) {
			if (import.meta.env.DEV) {
				return {
					tabs: new Map(),
					addTab: () => {},
					getTabIdByType: () => undefined,
				};
			}
			throw new Error("useTabContext must be used within a TabsProvider");
		}
		return context;
	};

	return { TabsProvider, useTabContext };
}
